import type { ZodTypeAny } from 'zod';
import { NODES_TO_PASTE_CONDITION_AFTER } from '@src/consts';
import { getBehaviorRulesSchema } from '@src/scenario-editor/validator/schemas/behavior-rules';
import { getIFStatementSchema } from '@src/scenario-editor/validator/schemas/if-statement';

/**
 * LF360-2100 — the rules that depend on the graph, one per condition node.
 *
 * An IF node may only compare against the options of the node it reads, and
 * a behaviour-rule node only against questions that exist. The editor's node
 * panels register these when they open; the headless check builds them for
 * every such node. Both take them from here.
 */

export interface ConditionOption {
  label: string;
  value: string;
}

type NodeData = Record<string, unknown> & { nodeType?: string };

// A valid slider has at most 101 values (its rule caps the range at 100). A
// scale with more is not a valid slider, and listing it would hang whatever
// checks a graph it was handed.
export const MAX_SLIDER_OPTIONS = 1000;

// What an IF can compare its source node's answer against: a slider's scale
// (min to max by increment), else the source's response options. A scale too
// long to list offers nothing; so does one whose step no longer moves the
// value (1e20 + 1 is 1e20), which would otherwise never end.
export const conditionSourceOptions = (source?: NodeData | null): ConditionOption[] => {
  if (!source) { return []; }
  if (source.nodeType === 'message:slider') {
    const min = Number(source.min);
    const max = Number(source.max);
    const incrementRaw = Number(source.increment);
    const increment = Number.isFinite(incrementRaw) && incrementRaw > 0 ? incrementRaw : 1;
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) { return []; }
    const options: ConditionOption[] = [];
    // Accumulated like the editor always has, so the values stay the ones IF
    // statements were saved with.
    for (let v = min; v <= max; v += increment) {
      if (options.length >= MAX_SLIDER_OPTIONS) { return []; }
      options.push({ label: String(v), value: String(v) });
    }
    return options;
  }
  return (source.response as ConditionOption[] | undefined) ?? [];
};

// The question texts a behaviour rule may name: every question node of the
// graph, the project's other scenario questions, and the project's tasks.
export const behaviorQuestions = (
  nodes: { data?: NodeData }[],
  allMessages: string[],
  taskIds: string[],
): string[] => [
  ...nodes
    .filter((node) => NODES_TO_PASTE_CONDITION_AFTER.includes(node.data?.nodeType ?? ''))
    .map((node) => node.data?.message as string)
    .filter(Boolean),
  ...allMessages.filter(Boolean),
  ...taskIds.filter(Boolean),
];

type Enum = [string, ...string[]];

export const ifConditionRule = (options: string[]): ZodTypeAny =>
  getIFStatementSchema({ possibleQuestions: options as unknown as Enum });

export const behaviorRule = (questions: string[]): ZodTypeAny =>
  getBehaviorRulesSchema({ possibleQuestions: questions as unknown as Enum, possibleAnswers: [] });
